import { useState } from "react";
import { ModalShell } from "../../components/ModalShell";
import { Skeleton } from "../../components/dashboard/Skeleton";
import { Toast, useToast } from "../../components/Toast";
import { CreateSecureNoteModal } from "../../components/dashboard/secure-notes/CreateSecureNoteModal";
import { type SecureNote, useSecureNotes } from "../../hooks/useSecureNotes";
import { formatDateTime } from "../../utils/formatDateTime";
import dialogStyles from "../admin/AdminFeatureFlags.module.css";
import modalStyles from "../../components/dashboard/secure-notes/SecureNoteModals.module.css";
import styles from "./SecureNotes.module.css";

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

export const expiresInLabel = (expiresAt: string, now: Date = new Date()): string => {
  const remaining = Date.parse(expiresAt) - now.getTime();
  if (remaining <= 0) return "expired";
  if (remaining < HOUR_MS) return "expires in under an hour";
  if (remaining < DAY_MS) return `expires in ${Math.round(remaining / HOUR_MS)}h`;
  const days = Math.ceil(remaining / DAY_MS);
  return `expires in ${days} day${days === 1 ? "" : "s"}`;
};

const expiresSoon = (expiresAt: string, now: Date = new Date()): boolean =>
  Date.parse(expiresAt) - now.getTime() < DAY_MS;

// Customer-facing secure-note handoff: send the ZTC team a secret without
// email. Write-only — the list shows metadata; values can never be read
// back from here.
export const SecureNotes = (): JSX.Element => {
  const { status, notes, errorMessage, createNote, deleteNote } = useSecureNotes();
  const { toast, showToast, dismissToast } = useToast();
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<SecureNote | null>(null);
  const [deleteBusy, setDeleteBusy] = useState<boolean>(false);

  const handleDelete = async (note: SecureNote): Promise<void> => {
    setDeleteBusy(true);
    const result = await deleteNote(note.id);
    setDeleteBusy(false);
    setDeleting(null);
    showToast(
      result.ok ? `Deleted "${note.name}".` : `Delete failed: ${result.message}`,
      result.ok ? "success" : "error",
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h1 className={styles.title}>Secure notes</h1>
          <p className={styles.subtitle}>
            Share API keys and other secrets with our team instead of emailing them. Notes delete
            themselves when they expire.
          </p>
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => setCreateOpen(true)}
          data-testid="new-secure-note"
        >
          New secure note
        </button>
      </header>

      {status === "loading" && (
        <ul className={styles.list} aria-hidden>
          {[0, 1, 2].map((i) => (
            <li key={i} className={styles.skeletonRow}>
              <Skeleton width="40%" height="1rem" />
              <Skeleton width="20%" height="1rem" />
            </li>
          ))}
        </ul>
      )}

      {status === "error" && (
        <div className={styles.errorCard}>{errorMessage ?? "Failed to load secure notes."}</div>
      )}

      {status === "ready" && notes.length === 0 && (
        <div className={styles.emptyCard} data-testid="secure-notes-empty">
          <span>No secure notes yet. Each one lasts up to 7 days, and you choose how long.</span>
        </div>
      )}

      {status === "ready" && notes.length > 0 && (
        <ul className={styles.list} data-testid="secure-notes-list">
          {notes.map((note) => (
            <li key={note.id} className={styles.row} data-testid="secure-note-row">
              <span className={styles.noteMain}>
                <span className={styles.noteName}>{note.name}</span>
                <span className={styles.noteMeta}>Shared {formatDateTime(note.createdAt)}</span>
              </span>
              <span className={styles.rowRight}>
                <span
                  className={
                    expiresSoon(note.expiresAt)
                      ? `${styles.expiryPill} ${styles.expirySoon}`
                      : styles.expiryPill
                  }
                  title={formatDateTime(note.expiresAt)}
                >
                  {expiresInLabel(note.expiresAt)}
                </span>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => setDeleting(note)}
                >
                  Delete
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {createOpen && (
        <CreateSecureNoteModal
          onClose={() => setCreateOpen(false)}
          onCreate={async (name, value, expiryDays) => {
            const result = await createNote(name, value, expiryDays);
            if (result.ok) {
              setCreateOpen(false);
              showToast(`Saved "${result.value.name}" — the ZeroToClaw team can now read it.`);
              return { ok: true, message: "" };
            }
            return { ok: false, message: result.message };
          }}
        />
      )}

      {deleting && (
        <ModalShell density="compact" onClose={() => setDeleting(null)} titleId="delete-secure-note-title" size="sm">
          <header className={dialogStyles.dialogHeader}>
            <h3 className={dialogStyles.dialogTitle} id="delete-secure-note-title">
              Delete secure note
            </h3>
            <button
              type="button"
              className={dialogStyles.closeBtn}
              onClick={() => setDeleting(null)}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <p className={modalStyles.hint}>
            &quot;{deleting.name}&quot; will be permanently deleted. The ZeroToClaw team
            won&apos;t be able to read it anymore.
          </p>
          <footer className={modalStyles.footer}>
            <button
              type="button"
              className={modalStyles.secondaryBtn}
              onClick={() => setDeleting(null)}
              disabled={deleteBusy}
            >
              Cancel
            </button>
            <button
              type="button"
              className={modalStyles.dangerBtn}
              onClick={() => void handleDelete(deleting)}
              disabled={deleteBusy}
            >
              {deleteBusy ? "Deleting…" : "Delete note"}
            </button>
          </footer>
        </ModalShell>
      )}

      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};
